import { cookieAsObject, getCookie } from '@susu/cookie'

let ga4MeasurementId: string | undefined

/**
 * Sets the GA4 measurement ID (e.g. 'G-XXXXXXXXXX'), used to derive the
 * `_ga_<container-id>` session cookie name. Call once during initialization.
 */
export const setGA4MeasurementId = (id: string): void => {
  ga4MeasurementId = id
}

// Each environment has its own GA4 property (same properties for headless and
// Shopify), keyed here by Segment write key — a build-time value, not derived
// from the request's hostname/URL, so this can't drift if that var is ever
// renamed. Unrecognized/production write keys fall back to production.
const GA4_MEASUREMENT_ID_BY_SEGMENT_WRITE_KEY: Record<string, string> = {
  Zy9h9uXcEt0hpJd9Xlz3AKbISpOdqBmN: 'G-52YBTD282B', // testing
  vu6HG9YzL0vWMDsSiBz9jbF3TNXuU2US: 'G-0XNN95Z7VJ', // acceptance
}
const PRODUCTION_SEGMENT_WRITE_KEY = '4OhwIsrqFzQ0aeuYev8muGSWiimGtGIG'
const PRODUCTION_GA4_MEASUREMENT_ID = 'G-2JHV4W0XRX'

/**
 * Resolves a Segment write key to its environment's GA4 measurement ID, so it
 * can be pre-set via setGA4MeasurementId before initializeSegment runs (see
 * DA-1340). Shared by every app rather than duplicated per-app, since the
 * mapping is identical everywhere. A new or rotated non-prod key would
 * otherwise silently resolve to production, so this warns (rather than
 * failing silently) when that fallback is actually hit for a write key that
 * isn't production's either.
 */
export const resolveGA4MeasurementIdForSegmentWriteKey = (
  segmentWriteKey: string,
): string => {
  const mappedId = GA4_MEASUREMENT_ID_BY_SEGMENT_WRITE_KEY[segmentWriteKey]
  if (mappedId) return mappedId

  if (segmentWriteKey !== PRODUCTION_SEGMENT_WRITE_KEY) {
    console.warn(
      '[ga4] Unmapped Segment write key for GA4 fallback — defaulting to production. ' +
        'Add it to GA4_MEASUREMENT_ID_BY_SEGMENT_WRITE_KEY if this is a real non-production environment.',
    )
  }
  return PRODUCTION_GA4_MEASUREMENT_ID
}

/**
 * Reads the GA4 client ID directly from the `_ga` cookie
 * (format: `GA1.1.<clientId part 1>.<clientId part 2>`).
 * @returns The client ID, or an empty string if the cookie is missing or malformed
 */
export const getGA4ClientIdFromCookie = (): string => {
  if (typeof window === 'undefined') {
    return ''
  }

  const value = getCookie('_ga')
  if (!value) {
    return ''
  }

  const parts = value.split('.')
  return parts.length >= 4 ? parts.slice(2).join('.') : ''
}

/**
 * Finds the GA4 session cookie name (`_ga_<container-id>`) by scanning all
 * cookies, so session data can still be read when no measurement ID has been
 * configured via setGA4MeasurementId.
 */
const findGA4SessionCookieName = (): string | undefined =>
  Object.keys(cookieAsObject()).find(
    name => name.startsWith('_ga_') && name !== '_ga',
  )

/**
 * Reads the GA4 session ID and session number directly from the
 * `_ga_<container-id>` cookie, handling both the GS2 (May 2025+,
 * dollar-delimited) and GS1 (legacy, dot-delimited) formats.
 * @param measurementId - GA4 measurement ID (e.g. 'G-XXXXXXXXXX'). Falls back to
 * scanning cookies by name when omitted.
 * @returns The session ID and session number, or empty strings if the cookie is missing
 */
export const getGA4SessionDataFromCookie = (
  measurementId: string,
): { sessionId: string; sessionNumber: string } => {
  if (typeof window === 'undefined') {
    return { sessionId: '', sessionNumber: '' }
  }

  const cookieName = measurementId
    ? `_ga_${measurementId.replace('G-', '')}`
    : findGA4SessionCookieName()
  const value = cookieName ? getCookie(cookieName) : undefined
  if (!value) {
    return { sessionId: '', sessionNumber: '' }
  }

  if (value.startsWith('GS2.')) {
    const paramsByKey = new Map(
      value
        .split('.')
        .slice(2)
        .join('.')
        .split('$')
        .map(param => [param.charAt(0), param.slice(1)] as const),
    )
    return {
      sessionId: paramsByKey.get('s') ?? '',
      sessionNumber: paramsByKey.get('o') ?? '',
    }
  }

  // GS1 (legacy): GS1.1.{sessionId}.{sessionNumber}.{...}
  const parts = value.split('.')
  return { sessionId: parts[2] ?? '', sessionNumber: parts[3] ?? '' }
}

/**
 * Get Google Analytics client/session data, read live from GA4's own cookies
 * (`_ga`, `_ga_<container-id>`) rather than a cached snapshot, so every event
 * reflects gtag's current session — including after GA rotates the session
 * cookie following 30 minutes of inactivity.
 */
export const getGoogleAnalyticsData = (): {
  ga_client_id: string
  ga_session_id: string
  ga_session_number: string
} => {
  if (typeof window === 'undefined') {
    return { ga_client_id: '', ga_session_id: '', ga_session_number: '' }
  }

  // getGA4SessionDataFromCookie can locate the session cookie by name even
  // when ga4MeasurementId hasn't been set yet (e.g. events fired before
  // initializeSegment runs), so there's no need for a localStorage fallback.
  // The app pre-sets ga4MeasurementId from its own segment write key before
  // initializeSegment runs, so this is rarely hit in practice (see DA-1340).
  const { sessionId, sessionNumber } = getGA4SessionDataFromCookie(
    ga4MeasurementId ?? '',
  )

  return {
    ga_client_id: getGA4ClientIdFromCookie(),
    ga_session_id: sessionId,
    ga_session_number: sessionNumber,
  }
}
